import { Fragment, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  formatMoney,
  formatPercent,
  formatQuantity,
  tickerOfSymbol,
  type AllocationReport,
  type OptionStrategy,
  type Holding,
  type Quote,
  type TransactionRow,
} from '@greencandle/api-client'
import { api } from '../api'
import { useAuth } from '../auth'
import { BADGE_FOR_CLASS, BADGE_FOR_TYPE, CLASS_RU, FLOW_RU } from '../terminal/dictionaries'
import { TerminalReports, type ReportsHandle } from './TerminalReports'
import { TransactionForm } from '../components/TransactionForm'
import { CustomAssetForm } from '../components/CustomAssetForm'
import { LiabilityForm } from '../components/LiabilityForm'
import { AddMenu, type AddChoice } from '../components/AddMenu'
import {
  Failed,
  Gap,
  PanelGap,
  Pending,
  StatCard,
  daysUntil,
  toneOf,
  type QueryLike,
} from '../terminal/primitives'
import {
  EquityTile,
  LiabilitiesTile,
  NetWorthTile,
  RegimeTile,
  YieldTile,
} from '../terminal/tiles'
import {
  isoDaysAgo,
  useAccounts,
  useAllocation,
  useHoldings,
  useThirtyDayChange,
} from '../terminal/queries'
import { RiskPanel } from '../terminal/panels/risk'
import { Optimiser, TrackRecord } from '../terminal/panels/optimiser'
import { StressPanel } from '../terminal/panels/stress'
import { TargetsPanel } from '../terminal/panels/targets'
import { GreeksStrip } from '../terminal/panels/greeks'
import { RehashTradesPanel } from '../terminal/panels/rehash'

/**
 * The multi-asset terminal, laid out to match `v2.1_greencandle.html`.
 *
 * <p>The mock is the specification for this screen: the same five tabs, the same three sub-tabs
 * under the equity desk, the same four-up stat rows, panels, tables and strategy cards, and its
 * own dark palette rather than the app's themed one.
 *
 * <p>What the mock cannot supply is data. Roughly two thirds of its figures - every liability,
 * every custom asset class, the optimiser's target weights, the forward cash-flow calendar - has
 * no table behind it, let alone an endpoint. None of those are filled in with a plausible number:
 * a figure the server cannot produce renders as an explicit gap naming the story that would
 * produce it, exactly as a missing price does under NFR-U4. A system of record that invents
 * $170,000 of mortgage to look finished is worse than one that admits the mortgage is not
 * modelled - the seed/import collision already showed what a confident wrong figure costs.
 */

type Tab = 'overview' | 'assets' | 'stocks' | 'crypto' | 'cashflow'
type StocksTab = 'summary' | 'spot' | 'options' | 'reports'

const TABS: ReadonlyArray<readonly [Tab, string]> = [
  ['overview', 'Overview'],
  ['assets', 'Assets & debts'],
  ['stocks', 'Equities'],
  ['crypto', 'Crypto'],
  ['cashflow', 'Cash flow'],
]

export function TerminalPage() {
  const [tab, setTab] = useState<Tab>('overview')
  const [accountId, setAccountId] = useState<string | null>(null)
  // What the + Add menu opened, or null. Held here rather than in the menu because the form
  // belongs under the toolbar, not inside it.
  const [adding, setAdding] = useState<AddChoice | null>(null)

  const accounts = useAccounts()
  const selected = accountId ?? accounts.data?.[0]?.id ?? null
  const account = accounts.data?.find((a) => a.id === selected) ?? accounts.data?.[0]

  if (accounts.isPending) return <p className="muted">Loading…</p>
  if (accounts.isError) return <Failed error={accounts.error} />
  if (!account || !selected) {
    return <div className="notice">No accounts yet. Import a statement to populate one.</div>
  }

  return (
    <div className="terminal">
      <div className="term-bar">
        <nav className="term-tabs" aria-label="Terminal sections">
          {TABS.map(([key, label]) => (
            <button key={key} type="button" aria-pressed={tab === key} onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </nav>

        <div className="term-bar-end">
          <select
            aria-label="Account"
            value={account.id}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {accounts.data.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.baseCurrency})
              </option>
            ))}
          </select>
          <AddMenu onChoose={setAdding} />
        </div>
      </div>

      {adding?.kind === 'txn' && (
        <TransactionForm
          accountId={selected}
          defaultType={adding.type}
          onDone={() => setAdding(null)}
        />
      )}
      {adding?.kind === 'asset' && <CustomAssetForm onDone={() => setAdding(null)} />}
      {adding?.kind === 'liability' && (
        <LiabilityForm accountId={selected} onDone={() => setAdding(null)} />
      )}

      {tab === 'overview' && <Overview accountId={selected} />}
      {tab === 'assets' && <AssetsAndDebts accountId={selected} />}
      {tab === 'stocks' && <Stocks accountId={selected} currency={account.baseCurrency} />}
      {tab === 'crypto' && <Crypto accountId={selected} />}
      {tab === 'cashflow' && <CashFlow accountId={selected} currency={account.baseCurrency} />}
    </div>
  )
}

// --------------------------------------------------------------------------- 1. Overview

function Overview({ accountId }: { accountId: string }) {
  const allocation = useAllocation(accountId)
  const holdings = useHoldings(accountId)
  const change = useThirtyDayChange(accountId)

  return (
    <>
      {/* The way into the audit. It sits above the tiles rather than in the toolbar because the
          audit is a sitting, not a glance: the toolbar holds things you do to the book, and this
          is a thing you do about it. */}
      <div className="audit-callout">
        <div>
          <strong>Run a portfolio audit</strong>
          <span className="muted">
            Five steps: what this money is for, what you actually hold, and what is outside the
            limits you set.
          </span>
        </div>
        <Link to="/wizard" className="btn-primary">
          Start the audit →
        </Link>
      </div>

      <div className="top-stats top-stats-5">
        <NetWorthTile accountId={accountId} />

        <StatCard label="Total assets">
          {allocation.data ? (
            <>
              <div className="value">
                {formatMoney(allocation.data.total, allocation.data.currency)}
              </div>
              <div className="sub">
                {allocation.data.holdingsValued} of {allocation.data.holdingsConsidered} holdings
                valued
                {allocation.data.holdingsConsidered > allocation.data.holdingsValued
                  ? ` · ${
                      allocation.data.holdingsConsidered - allocation.data.holdingsValued
                    } outside this total`
                  : ''}
              </div>
            </>
          ) : (
            <Pending query={allocation} />
          )}
        </StatCard>

        <LiabilitiesTile accountId={accountId} />

        <StatCard label="Change, 30 days">
          {change.data ? (
            change.data.percent == null ? (
              <Gap text={change.data.note ?? 'Not enough snapshots'} />
            ) : (
              <>
                <div className={`value ${toneOf(change.data.percent)}`}>
                  {formatPercent(change.data.percent)}
                </div>
                <div className="sub">since {change.data.from}</div>
              </>
            )
          ) : (
            <Pending query={change} />
          )}
        </StatCard>

        <RegimeTile accountId={accountId} />
      </div>

      <div className="grid-2">
        <section className="panel">
          <div className="panel-header">
            <h3>Multi-asset portfolio</h3>
            <span className="panel-note">
              Base currency: {allocation.data?.currency ?? '—'}
            </span>
          </div>
          {allocation.isError ? (
            <Failed error={allocation.error} />
          ) : allocation.data ? (
            <ClassTable report={allocation.data} holdings={holdings.data} />
          ) : (
            <Pending query={allocation} />
          )}
        </section>

        <TargetsPanel accountId={accountId} />
      </div>
    </>
  )
}

// ------------------------------------------------------------------ 2. Assets and debts

function AssetsAndDebts({ accountId }: { accountId: string }) {
  const allocation = useAllocation(accountId)
  const holdings = useHoldings(accountId)
  const byAccount = useQuery({
    queryKey: ['allocation', 'ACCOUNT'],
    queryFn: () => api.allocation({ dimension: 'ACCOUNT' }),
  })

  const custom = (holdings.data ?? []).filter((h) => h.assetClass === 'CUSTOM').length

  return (
    <>
      <div className="top-stats">
        <StatCard label="Total assets">
          {allocation.data ? (
            <>
              <div className="value">
                {formatMoney(allocation.data.total, allocation.data.currency)}
              </div>
              <div className="sub">Only what could be valued</div>
            </>
          ) : (
            <Pending query={allocation} />
          )}
        </StatCard>

        <LiabilitiesTile accountId={accountId} label="Debt principal (loans)" />

        <EquityTile accountId={accountId} />

        <StatCard label="Custom assets">
          <div className="value">{custom}</div>
          <div className="sub">
            {custom === 0
              ? 'The CUSTOM class exists but nothing is held in it'
              : 'positions held in the CUSTOM class'}
          </div>
        </StatCard>
      </div>

      <section className="panel">
        <div className="panel-header">
          <h3>Asset registry and attached debt</h3>
          <span className="panel-note">AST-1 · custom types not built</span>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Valuation</th>
                <th className="right">Market value</th>
                <th className="right">Attached debt</th>
                <th className="right">Equity</th>
              </tr>
            </thead>
            <tbody>
              {byAccount.isPending && (
                <tr>
                  <td colSpan={5} className="muted">
                    Loading…
                  </td>
                </tr>
              )}
              {byAccount.data?.buckets.map((bucket) => (
                <tr key={bucket.key}>
                  <td>
                    <strong>{bucket.key}</strong>
                    <div className="cell-sub">
                      {bucket.holdings} position{bucket.holdings === 1 ? '' : 's'}
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-stock">Live Market API</span>
                  </td>
                  <td className="right num">
                    {formatMoney(bucket.value, byAccount.data.currency)}
                  </td>
                  <td className="right">
                    <span className="gap-inline">not modelled</span>
                  </td>
                  <td className="right num">
                    <strong>{formatMoney(bucket.value, byAccount.data.currency)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="panel-foot">
          The mock lists an apartment against a mortgage and a P2P pool beside the broker account.
          <code> instruments.asset_class</code> is a closed six-value enum with check
          constraints, so neither can be held at all until AST-1 reopens it.
        </p>
      </section>

      <RiskPanel accountId={accountId} />
    </>
  )
}


function Stocks({ accountId, currency }: { accountId: string; currency: string }) {
  const { features } = useAuth()
  const [sub, setSub] = useState<StocksTab>('summary')
  // FR-B11. Open over whichever sub-tab is showing, so it is one click from anywhere on the desk.
  const [rehashing, setRehashing] = useState(false)
  const reports = useRef<ReportsHandle>(null)
  const allocation = useAllocation(accountId)
  const holdings = useHoldings(accountId)

  // Options included on purpose: they group under the ticker they are written on, so the NFLX
  // block holds the shares and every NFLX contract together.
  const spot = (holdings.data ?? []).filter(
    (h) => h.assetClass === 'STOCK' || h.assetClass === 'ETF' || h.assetClass === 'OPTION',
  )

  return (
    <>
      <div className="sub-nav">
        <button type="button" aria-pressed={sub === 'summary'} onClick={() => setSub('summary')}>
          Equity summary (stocks + ETFs + options)
        </button>
        <button type="button" aria-pressed={sub === 'spot'} onClick={() => setSub('spot')}>
          Stocks & ETFs
        </button>
        <button type="button" aria-pressed={sub === 'options'} onClick={() => setSub('options')}>
          Options
        </button>
        <button type="button" aria-pressed={sub === 'reports'} onClick={() => setSub('reports')}>
          Statements
        </button>

        {/* FR-K9. The briefing measures exactly this desk - its exposure, structures and limits -
            so the way in sits here, first of the actions. With the feature off the link goes
            rather than being disabled: a control that cannot do anything is worse than no
            control, and the route behind it is gone too. */}
        {features.briefing && (
          <Link to="/briefing" className="btn-primary sub-nav-action">
            Разбор
          </Link>
        )}

        {/* FR-B11. The one-off that has to run before the first CSV statement is committed over
            PDF history; beside the button that loads one, because that is when it matters. */}
        <button
          type="button"
          aria-pressed={rehashing}
          onClick={() => setRehashing(!rehashing)}
        >
          Rehash trades
        </button>

        {/* Straight to the file picker, wherever you are in this tab. Everything on the equity
            desk comes from a statement, so the shortest path to loading one belongs here. */}
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setSub('reports')
            // After the switch, so the input exists to be clicked.
            window.setTimeout(() => reports.current?.choose(), 0)
          }}
        >
          + Load statement
        </button>
      </div>

      {rehashing && <RehashTradesPanel onClose={() => setRehashing(false)} />}

      {sub === 'summary' && (
        <>
          <div className="top-stats">
            <ClassCard label="Stocks" bucket="STOCK" report={allocation.data}
                       holdings={holdings.data} query={allocation} />
            <ClassCard label="ETF" bucket="ETF" report={allocation.data}
                       holdings={holdings.data} query={allocation} />
            <ClassCard label="Options" bucket="OPTION" report={allocation.data}
                       holdings={holdings.data} query={allocation} />
            <YieldTile accountId={accountId} />
          </div>

          <div className="grid-3-1">
            <div className="term-stack">
              <Optimiser accountId={accountId} />
              <TrackRecord accountId={accountId} />
              <StressPanel accountId={accountId} />
            </div>

            <Movers accountId={accountId} title="Movers" filter={() => true} />
          </div>
        </>
      )}

      {sub === 'spot' && (
        <section className="panel">
          <div className="panel-header">
            <h3>Positions by ticker</h3>
            <span className="panel-note">
              {spot.length} position{spot.length === 1 ? '' : 's'}
            </span>
          </div>
          {holdings.isError ? (
            <Failed error={holdings.error} />
          ) : holdings.isPending ? (
            <Pending query={holdings} />
          ) : (
            <SpotTable rows={spot} accountId={accountId} />
          )}
        </section>
      )}

      {sub === 'options' && <OptionDesk accountId={accountId} currency={currency} />}

      {sub === 'reports' && <TerminalReports ref={reports} />}
    </>
  )
}

function OptionDesk({ accountId, currency }: { accountId: string; currency: string }) {
  const strategies = useQuery({
    queryKey: ['options-analysis', accountId],
    queryFn: () => api.optionsAnalysis(accountId),
  })

  if (strategies.isError) return <Failed error={strategies.error} />
  if (strategies.isPending) return <p className="muted">Loading…</p>

  return (
    <section className="panel">
      <div className="panel-header">
        <h3>Option desk (open strategies)</h3>
        <span className="panel-note">
          {strategies.data.length} open {strategies.data.length === 1 ? 'strategy' : 'strategies'}
        </span>
      </div>

      <GreeksStrip accountId={accountId} />

      <div className="opt-card-grid">
        {strategies.data.map((s, i) => {
          const credit = Number(s.netCost) < 0
          return (
            <article className="opt-card" key={`${s.underlying}-${s.expiry}-${i}`}>
              <div className="opt-card-head">
                <strong>
                  {s.underlying} — {s.label}
                </strong>
                <span className={`badge ${credit ? 'badge-re' : 'badge-stock'}`}>
                  {credit ? 'Credit' : 'Debit'}
                </span>
              </div>

              <div className="opt-legs">
                {s.legs
                  .map(
                    (leg) =>
                      `${Number(leg.quantity) < 0 ? 'Short' : 'Long'} ${Math.abs(
                        Number(leg.quantity),
                      )} ${formatQuantity(leg.strike)} ${leg.right}`,
                  )
                  .join(' • ')}
              </div>

              <div className="opt-row">
                <span>{credit ? 'Net Credit:' : 'Net Debit:'}</span>
                <strong>{formatMoney(String(Math.abs(Number(s.netCost))), currency)}</strong>
              </div>
              <div className="opt-row">
                <span>Max Profit:</span>
                {s.maxProfitUnbounded ? (
                  <span className="up">unlimited</span>
                ) : (
                  <span className="up">{formatMoney(s.maxProfit, currency)}</span>
                )}
              </div>
              <div className="opt-row">
                <span>Max Loss:</span>
                {s.maxLossUnbounded ? (
                  <span className="down">unlimited</span>
                ) : (
                  <span className="down">{formatMoney(s.maxLoss, currency)}</span>
                )}
              </div>
              <div className="opt-row">
                <span>Expiry:</span>
                <span className="num">
                  {s.expiry}
                  {s.legs.length > 0 && daysUntil(s.expiry) != null
                    ? ` · in ${daysUntil(s.expiry)}d`
                    : ''}
                </span>
              </div>
            </article>
          )
        })}
      </div>

      {strategies.data.length === 0 && (
        <p className="panel-foot">No option positions in this account.</p>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------- 4. Crypto

function Crypto({ accountId }: { accountId: string }) {
  const allocation = useAllocation(accountId)
  const holdings = useHoldings(accountId)
  const crypto = (holdings.data ?? []).filter((h) => h.assetClass === 'CRYPTO')

  return (
    <>
      <div className="top-stats">
        <ClassCard label="Crypto" bucket="CRYPTO" report={allocation.data}
                   holdings={holdings.data} query={allocation} />

        <StatCard label="Share of portfolio">
          {allocation.data ? (
            (() => {
              const bucket = allocation.data.buckets.find((b) => b.key === 'CRYPTO')
              return bucket ? (
                <>
                  <div className="value">{formatPercent(bucket.weightPercent)}</div>
                  <div className="sub">Of the priced total, not of net worth</div>
                </>
              ) : (
                <Gap text={crypto.length > 0 ? 'Crypto is held but could not be valued' : 'No crypto is held'} />
              )
            })()
          ) : (
            <Pending query={allocation} />
          )}
        </StatCard>

        <StatCard label="Staking (yield)">
          <Gap story="AST-1" text="staked balances are not tracked separately" />
        </StatCard>

        <StatCard label="Custody (cold / exchange)">
          <Gap story="AST-1" text="an instrument has no custody field" />
        </StatCard>
      </div>

      <div className="grid-3-1">
        <section className="panel">
          <div className="panel-header">
            <h3>Crypto balances</h3>
            <span className="panel-note">Coinbase · daily closes</span>
          </div>
          {holdings.isError ? (
            <Failed error={holdings.error} />
          ) : holdings.isPending ? (
            <Pending query={holdings} />
          ) : crypto.length === 0 ? (
            <p className="panel-foot">
              No crypto in this account. The Coinbase provider is wired up and would value it the
              night it arrives.
            </p>
          ) : (
            <SpotTable rows={crypto} accountId={accountId} />
          )}
        </section>

        <Movers
          accountId={accountId}
          title="Movers (crypto)"
          filter={(h) => h.assetClass === 'CRYPTO'}
        />
      </div>
    </>
  )
}

// ------------------------------------------------------------------ 5. Cash flow

const INCOME_TYPES = new Set(['DIVIDEND', 'INTEREST', 'STAKING_REWARD', 'RENTAL_INCOME'])

/** The order the overview lists them in, rather than whatever order the ledger happened to. */
const INCOME_ORDER = ['DIVIDEND', 'INTEREST', 'STAKING_REWARD', 'RENTAL_INCOME']

function CashFlow({ accountId, currency }: { accountId: string; currency: string }) {
  const from = useMemo(() => isoDaysAgo(365), [])
  const transactions = useQuery({
    queryKey: ['transactions', accountId, from],
    queryFn: () => api.transactions(accountId, { from }),
  })
  const [expandedType, setExpandedType] = useState<string | null>(null)
  const [editing, setEditing] = useState<TransactionRow | null>(null)
  const queryClient = useQueryClient()

  // Only hand-entered, non-dividend rows can go; the server decides which and says why, and
  // the buttons below are disabled from its answer rather than from a rule repeated here.
  const remove = useMutation({
    mutationFn: (row: TransactionRow) => api.deleteTransaction(accountId, row.id),
    onSuccess: () => void queryClient.invalidateQueries(),
  })

  const income = (transactions.data ?? []).filter((t) => INCOME_TYPES.has(t.type))
  const byType = useMemo(() => {
    const map = new Map<string, TransactionRow[]>()
    for (const row of income) map.set(row.type, [...(map.get(row.type) ?? []), row])
    return map
  }, [transactions.data])

  return (
    <>
      <div className="top-stats">
        <StatCard label="Net monthly inflow">
          <Gap story="UI-3" text="needs declared schedules, not recorded history" />
        </StatCard>
        <StatCard label="Dividends, 12 months">
          <IncomeTotal rows={byType.get('DIVIDEND')} currency={currency} />
          <div className="sub">Actually received, from the ledger</div>
        </StatCard>
        <StatCard label="Interest, 12 months">
          <IncomeTotal rows={byType.get('INTEREST')} currency={currency} />
          <div className="sub">Broker interest on cash balances</div>
        </StatCard>
        <StatCard label="Rent, 12 months">
          <IncomeTotal rows={byType.get('RENTAL_INCOME')} currency={currency} />
          <div className="sub">A P2P loan's coupon counts under Interest above, not here</div>
        </StatCard>
      </div>

      {editing && (
        // The same form the transactions page uses. Correcting a payment where it is being
        // read beats sending someone to another screen to find the row again.
        <TransactionForm
          accountId={accountId}
          existing={editing}
          onDone={() => setEditing(null)}
        />
      )}

      <section className="panel">
        <div className="panel-header">
          <h3>Income received</h3>
          <span className="panel-note">
            Last 12 months · by kind · expand a kind to correct or remove a payment
          </span>
        </div>
        {transactions.isError ? (
          <Failed error={transactions.error} />
        ) : transactions.isPending ? (
          <Pending query={transactions} />
        ) : income.length === 0 ? (
          <p className="panel-foot">No dividend, interest, staking or rental rows in the last year.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Kind</th>
                  <th className="right">Payments</th>
                  <th className="right">Total</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {INCOME_ORDER.filter((type) => byType.has(type)).map((type) => (
                  <IncomeKindRow
                    onDelete={(row) => remove.mutate(row)}
                    onEdit={(row) => setEditing(row)}
                    key={type}
                    type={type}
                    rows={byType.get(type) ?? []}
                    expanded={expandedType === type}
                    onToggle={() => setExpandedType(expandedType === type ? null : type)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <div className="panel-header">
          <h3>Forward payment calendar</h3>
          <span className="panel-note">Next 30 days</span>
        </div>
        <PanelGap
          stories={[
            ['FR-E7', 'the dividend calendar'],
            ['UI-3', 'rent, coupons, staking and interest in one view'],
          ]}
        >
          The table above looks backwards, because that is what the ledger can answer. The mock
          looks forward — next month's rent, the mortgage annuity, an upcoming MSFT dividend.
          That needs declared schedules rather than recorded history.
        </PanelGap>
      </section>
    </>
  )
}

// ------------------------------------------------------------------------------ tables

function ClassTable({
  report,
  holdings,
}: {
  report: AllocationReport
  holdings: Holding[] | undefined
}) {
  const held = new Map<string, number>()
  for (const h of holdings ?? []) held.set(h.assetClass, (held.get(h.assetClass) ?? 0) + 1)
  const shown = new Set(report.buckets.map((b) => b.key))

  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Asset / class</th>
              <th>Category</th>
              <th className="right">Value</th>
              <th className="right">Weight</th>
            </tr>
          </thead>
          <tbody>
            {report.buckets.map((bucket) => (
              <tr key={bucket.key}>
                <td>
                  <strong>{CLASS_RU[bucket.key] ?? bucket.key}</strong>
                  <div className="cell-sub">
                    {bucket.holdings} position{bucket.holdings === 1 ? '' : 's'}
                  </div>
                </td>
                <td>
                  <span className={`badge ${BADGE_FOR_CLASS[bucket.key] ?? 'badge-custom'}`}>
                    {CLASS_RU[bucket.key] ?? bucket.key}
                  </span>
                </td>
                <td className="right num">{formatMoney(bucket.value, report.currency)}</td>
                <td className="right num">{formatPercent(bucket.weightPercent)}</td>
              </tr>
            ))}

            {/* Held but unvalued. Absent from the report for a quite different reason than an
                empty class, and a zero here would state that 24 real contracts do not exist. */}
            {[...held.entries()]
              .filter(([key]) => !shown.has(key))
              .map(([key, count]) => (
                <tr key={key} className="row-gap">
                  <td>
                    <strong>{CLASS_RU[key] ?? key}</strong>
                    <div className="cell-sub">
                      {count} position{count === 1 ? '' : 's'}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${BADGE_FOR_CLASS[key] ?? 'badge-custom'}`}>
                      {CLASS_RU[key] ?? key}
                    </span>
                  </td>
                  <td className="right">
                    <span className="gap-inline">no price</span>
                  </td>
                  <td className="right">
                    <span className="gap-inline">—</span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {report.unvalued[0] && (
        <p className="panel-foot warn">
          {report.unvalued.length} holding{report.unvalued.length === 1 ? '' : 's'} left the
          denominator:{' '}
          {report.unvalued[0].reason}.
        </p>
      )}
    </>
  )
}

/** The ticker a holding belongs under: the underlying for an option, the symbol otherwise. */
function tickerOf(holding: Holding): string {
  return holding.option ? holding.option.underlyingSymbol : holding.symbol
}

/**
 * The strategy heading each option instrument sits under, keyed by its OCC symbol.
 *
 * <p>A symbol can legitimately belong to two strategies at once: 100 shares covering 1 of 2
 * written calls splits that leg into a covered call and a leftover single leg. The position is
 * still one row with one quantity, so both names are kept and joined rather than one being
 * picked - a row that claims to be only the covered call would be describing half of itself.
 */
function strategyNames(strategies: OptionStrategy[] | undefined): Map<string, string> {
  const labels = new Map<string, string[]>()
  for (const strategy of strategies ?? []) {
    for (const leg of strategy.legs) {
      const seen = labels.get(leg.symbol) ?? []
      if (!seen.includes(strategy.label)) {
        labels.set(leg.symbol, [...seen, strategy.label])
      }
    }
  }
  return new Map([...labels].map(([symbol, names]) => [symbol, names.join(' + ')]))
}

/**
 * Holdings grouped by ticker, and each ticker's options grouped again by the strategy they form.
 *
 * <p>A portfolio is held in companies, not in contracts. Twenty-four option rows spelled out as
 * 21-character OCC symbols is a list nobody scans; the same positions under nine tickers is a
 * portfolio. The contracts are still there, one line each, under the ticker they belong to.
 *
 * <p>Within a ticker, the contracts sit under the name of the structure they actually make - a
 * collar, a vertical spread - because that is the position. Two legs of a spread listed
 * separately invite reading each one's P&L as a result, when only the pair has one. The rows
 * themselves are never split or duplicated to fit a heading: one instrument is one row with one
 * quantity, so every subtotal stays exactly the sum of what is above it.
 *
 * <p>Subtotals only ever add what was actually valued, and the group says when it is covering
 * less than it holds - the same rule the page-level totals follow (FR-E4).
 */
function SpotTable({ rows, accountId }: { rows: Holding[]; accountId: string }) {
  // Shares the Option desk's cache entry: same account, same query key, one request.
  const strategies = useQuery({
    queryKey: ['options-analysis', accountId],
    queryFn: () => api.optionsAnalysis(accountId),
    enabled: rows.some((r) => r.option != null),
  })

  const names = useMemo(() => strategyNames(strategies.data), [strategies.data])

  const groups = useMemo(() => {
    const byTicker = new Map<string, Holding[]>()
    for (const row of rows) {
      const ticker = tickerOf(row)
      byTicker.set(ticker, [...(byTicker.get(ticker) ?? []), row])
    }

    return [...byTicker.entries()]
      .map(([ticker, members]) => {
        const valued = members.filter((m) => m.marketValue != null)
        // Same-currency addition of figures the server produced. A group spanning currencies
        // reports no subtotal rather than adding dollars to euros.
        const currencies = new Set(valued.map((m) => m.currency))
        const total =
          valued.length > 0 && currencies.size === 1
            ? valued.reduce((sum, m) => sum + Number(m.marketValue), 0)
            : null

        // Options sit under the structure they form; everything else sits directly under the
        // ticker. An option the detector has not named yet - the analysis is still loading, or
        // it failed - falls under "Options" rather than vanishing from the table.
        const plain = members.filter((m) => !m.option)
        const byStrategy = new Map<string, Holding[]>()
        for (const member of members.filter((m) => m.option)) {
          const label = names.get(member.symbol) ?? 'Options'
          byStrategy.set(label, [...(byStrategy.get(label) ?? []), member])
        }

        return {
          ticker,
          members,
          plain,
          strategies: [...byStrategy.entries()].map(([label, legs]) => ({ label, legs })),
          total,
          currency: valued[0]?.currency ?? members[0]?.currency ?? 'USD',
          unvalued: members.length - valued.length,
          // The company name lives on the plain holding; an option only carries its contract.
          name: plain[0]?.name ?? '',
        }
      })
      .sort((a, b) => (b.total ?? -1) - (a.total ?? -1))
  }, [rows, names])

  const unvalued = rows.filter((r) => r.marketValue == null).length

  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Ticker</th>
              <th className="right">Quantity</th>
              <th className="right">Avg cost</th>
              <th className="right">Price</th>
              <th className="right">Market value</th>
              <th className="right">Unrealised</th>
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.ticker}>
              <tr className="group-row">
                <td>
                  <strong>{group.ticker}</strong>
                  {group.name && <span className="cell-sub"> {group.name}</span>}
                </td>
                <td className="right cell-sub">
                  {group.members.length} position{group.members.length === 1 ? '' : 's'}
                  {group.unvalued > 0 ? ` · ${group.unvalued} unpriced` : ''}
                </td>
                <td colSpan={2} />
                <td className="right num">
                  <strong>
                    {group.total == null ? (
                      <span className="gap-inline">no price</span>
                    ) : (
                      formatMoney(String(group.total), group.currency)
                    )}
                  </strong>
                </td>
                <td />
              </tr>

              {group.plain.map((row) => (
                <MemberRow key={row.instrumentId} row={row} />
              ))}

              {group.strategies.map((strategy) => (
                <Fragment key={strategy.label}>
                  <tr className="strategy-row">
                    <td colSpan={6}>
                      <span className="strategy-name">{strategy.label}</span>
                      <span className="cell-sub">
                        {' '}
                        {strategy.legs.length} leg{strategy.legs.length === 1 ? '' : 's'}
                      </span>
                    </td>
                  </tr>
                  {strategy.legs.map((row) => (
                    <MemberRow key={row.instrumentId} row={row} />
                  ))}
                </Fragment>
              ))}
            </tbody>
          ))}
        </table>
      </div>
      {unvalued > 0 && (
        <p className="panel-foot warn">
          {unvalued} position{unvalued === 1 ? '' : 's'} could not be valued and {unvalued === 1
            ? 'is'
            : 'are'} in no total on this page.
        </p>
      )}
    </>
  )
}

/** One position's line. The ticker is on the group row above, so the cell names the contract
 *  for an option and the asset class for anything else - repeating the ticker reads as noise. */
function MemberRow({ row }: { row: Holding }) {
  return (
    <tr>
      <td className="member">
        {row.option ? row.name : <span className="cell-sub">{row.assetClass}</span>}
      </td>
      <td className="right num">{formatQuantity(row.quantity)}</td>
      <td className="right num">{formatMoney(row.averageCost, row.currency)}</td>
      <td className="right num">
        {formatMoney(row.lastPrice, row.currency) ?? (
          <span className="gap-inline" title={row.priceNote ?? undefined}>
            no price
          </span>
        )}
      </td>
      <td className="right num">
        {formatMoney(row.marketValue, row.currency) ?? (
          <span className="gap-inline" title={row.priceNote ?? undefined}>
            no price
          </span>
        )}
      </td>
      <td className={`right num ${toneOf(row.unrealisedPnl)}`}>
        {formatMoney(row.unrealisedPnl, row.currency) ?? <span className="gap-inline">—</span>}
        {row.unrealisedPnlPercent != null && (
          <span className="cell-sub"> ({formatPercent(row.unrealisedPnlPercent)})</span>
        )}
      </td>
    </tr>
  )
}

function Movers({
  accountId,
  title,
  filter,
}: {
  accountId: string
  title: string
  filter: (h: Holding) => boolean
}) {
  const holdings = useHoldings(accountId)
  const quotes = useQuery({
    queryKey: ['quotes', accountId],
    queryFn: () => api.quotes(accountId),
    refetchInterval: 60_000,
  })

  const rows = useMemo(() => {
    const quoteFor = new Map<string, Quote>()
    for (const q of quotes.data ?? []) quoteFor.set(q.instrumentId, q)

    return (holdings.data ?? [])
      .filter(filter)
      .flatMap((h) => {
        const quote = quoteFor.get(h.instrumentId)
        if (!quote || h.lastPrice == null) return []
        const close = Number(h.lastPrice)
        if (close === 0) return []
        return [{ h, quote, move: ((Number(quote.price) - close) / close) * 100 }]
      })
      .sort((a, b) => Math.abs(b.move) - Math.abs(a.move))
      .slice(0, 6)
  }, [holdings.data, quotes.data])

  const delayed = quotes.data?.find((q) => q.delayMinutes > 0)

  return (
    <section className="panel">
      <div className="panel-header">
        <h3>{title}</h3>
        <span className="panel-note">
          {delayed ? `${delayed.delayMinutes} min delayed` : 'quote against close'}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="panel-foot">No instrument here has both a live quote and a stored close.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Ticker</th>
                <th className="right">Price</th>
                <th className="right">Change</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ h, quote, move }) => (
                <tr key={h.instrumentId}>
                  <td>
                    {/* The ticker, not the 21-character OCC symbol. An option still needs
                        telling apart from its own stock, so the contract goes underneath in
                        the readable form rather than back into the heading. */}
                    <strong>{tickerOf(h)}</strong>
                    {h.option && <span className="cell-sub"> {h.name}</span>}
                  </td>
                  <td className="right num">{formatMoney(quote.price, quote.currency)}</td>
                  <td className={`right num ${toneOf(String(move))}`}>{move.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="panel-foot">
        The mock shows S&amp;P 500 movers. No endpoint serves one — quotes are scoped to what
        the account holds, so this is your own positions against their last stored close.
      </p>
    </section>
  )
}

// ----------------------------------------------------- FR-E7: trailing income on the desk

/**
 * Yield on cost, from the ledger.
 *
 * <p>Replaces a tile that read "not available" for as long as this screen has existed. The
 * forward dividend calendar FR-E7 also asks for still needs declared schedules nothing supplies,
 * but the trailing half is a measurement rather than a promise, and it is the half that is true.
 *
 * <p>On cost rather than on price on purpose: a stock bought at 40 paying 2 yields 5% on cost
 * forever and 2% once it reaches 100. The first says whether the original decision is still
 * earning; the second says what a buyer today would get. Only one of those is about this
 * portfolio.
 */
// ------------------------------------------------------ AST-2: net worth, debts, equity


// ------------------------------------------------------------------------ small pieces

/**
 * One asset class, telling "nothing held" apart from "nothing valued".
 *
 * The allocation report only carries classes it could value, so a class is absent for two quite
 * different reasons. Rendering the second as a zero states, of a real position, that it does not
 * exist - the holdings list is the second opinion that separates them.
 */
function ClassCard({
  label,
  bucket,
  report,
  holdings,
  query,
}: {
  label: string
  bucket: string
  report: AllocationReport | undefined
  holdings: Holding[] | undefined
  query: QueryLike
}) {
  const found = report?.buckets.find((b) => b.key === bucket)
  const held = (holdings ?? []).filter((h) => h.assetClass === bucket)

  return (
    <StatCard label={label}>
      {!report ? (
        <Pending query={query} />
      ) : found ? (
        <>
          <div className="value">{formatMoney(found.value, report.currency)}</div>
          <div className="sub">
            {found.holdings} position{found.holdings === 1 ? '' : 's'} ·{' '}
            {formatPercent(found.weightPercent)} of the portfolio
            {held.length > found.holdings ? ` · ${held.length - found.holdings} unpriced` : ''}
          </div>
        </>
      ) : held.length > 0 ? (
        <Gap
          text={`${held.length} position${held.length === 1 ? '' : 's'} held, none of which could be valued`}
        />
      ) : (
        <>
          <div className="value faint">{formatMoney('0', report.currency)}</div>
          <div className="sub">Nothing held in this class</div>
        </>
      )}
    </StatCard>
  )
}

/** A figure the server cannot produce, with the story that would produce it. */
/** A whole panel the mock shows and no endpoint can answer. */
/**
 * One kind of income - dividends, interest, staking - as a single overview line, with the
 * payment-by-payment history behind a Details toggle.
 *
 * <p>Grouped by kind rather than by holding, and that is not only a presentation choice: an
 * imported dividend carries no instrument at all. `StatementCommitService` persists every cash
 * flow with a null instrument id, so the API has no symbol to report and a per-holding grouping
 * had nothing to group by - it fell back to transaction ids and rendered a wall of UUIDs, one
 * payment each. Per-holding attribution needs the importer to resolve the instrument first.
 *
 * <p>Same expand pattern as HoldingsPage's lots row: a toggle button carrying the arrow glyph,
 * and a second `<tr>` of the same colSpan holding the detail when open.
 */
function IncomeKindRow({
  type,
  rows,
  expanded,
  onToggle,
  onDelete,
  onEdit,
}: {
  type: string
  rows: TransactionRow[]
  expanded: boolean
  onToggle: () => void
  onDelete: (row: TransactionRow) => void
  onEdit: (row: TransactionRow) => void
}) {
  const head = rows[0]
  if (!head) {
    return null
  }
  // Same-currency addition of figures the server already produced, and the same refusal to add
  // across currencies that IncomeTotal and the backend both make.
  const mixedCurrency = rows.some((r) => r.currency !== head.currency)
  const total = mixedCurrency
    ? null
    : rows.reduce((sum, r) => sum + Number(r.amount ?? 0), 0)

  return (
    <>
      <tr>
        <td>
          <button type="button" className="linklike" onClick={onToggle} aria-expanded={expanded}>
            {expanded ? '▾' : '▸'}{' '}
            <span className={`badge ${BADGE_FOR_TYPE[type] ?? 'badge-stock'}`}>
              {FLOW_RU[type] ?? type}
            </span>
          </button>
        </td>
        <td className="right num">{rows.length}</td>
        <td className={`right num ${total == null ? '' : toneOf(String(total))}`}>
          {mixedCurrency ? (
            <Gap text="rows in more than one currency" />
          ) : (
            <strong>{formatMoney(String(total), head.currency)}</strong>
          )}
        </td>
        <td className="right">
          <button type="button" className="linklike" onClick={onToggle} aria-expanded={expanded}>
            Details
          </button>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={4} style={{ background: 'var(--bg-inset)' }}>
            <table style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Asset / source</th>
                  <th className="right">Amount</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {[...rows]
                  .sort((a, b) => b.tradeDate.localeCompare(a.tradeDate))
                  .map((row) => (
                    <tr key={row.id}>
                      <td className="num">{row.tradeDate}</td>
                      {/* The ticker where the payment names a holding; otherwise the line the
                          broker printed, which is what an interest credit or a payment on
                          something never held has instead of one. An em dash only when there is
                          neither - a row entered by hand with nothing said about it. */}
                      <td>
                        {row.symbol ? (
                          tickerOfSymbol(row.symbol)
                        ) : row.note ? (
                          <span className="cell-sub">{row.note}</span>
                        ) : (
                          <span className="cell-sub">—</span>
                        )}
                      </td>
                      <td className={`right num ${toneOf(row.amount)}`}>
                        {formatMoney(row.amount, row.currency)}
                      </td>
                      {/* Disabled with the server's reason rather than hidden - a row that
                          silently lacks a button looks broken, one that says why has taught
                          the rule. */}
                      <td className="right" style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="linklike"
                          disabled={!row.editable}
                          title={row.notEditableBecause ?? undefined}
                          onClick={() => onEdit(row)}
                        >
                          Edit
                        </button>{' '}
                        <button
                          type="button"
                          className="linklike"
                          disabled={!row.editable}
                          title={row.notEditableBecause ?? undefined}
                          onClick={() => onDelete(row)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  )
}

function IncomeTotal({ rows, currency }: { rows: TransactionRow[] | undefined; currency: string }) {
  const head = rows?.[0]
  if (!rows || !head) {
    return <div className="value faint">{formatMoney('0', currency)}</div>
  }
  // Same-currency addition of figures the server already produced. Cross-currency totals go
  // through the allocation endpoint - the backend refuses to add currencies and so does this.
  if (rows.some((r) => r.currency !== head.currency)) {
    return <Gap text="rows in more than one currency" />
  }
  const total = rows.reduce((sum, r) => sum + Number(r.amount ?? 0), 0)
  return <div className="value up">{formatMoney(String(total), head.currency)}</div>
}

// --------------------------------------------------------------------------------- data
